"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BrandMark } from "./brand";

const INVENTORY_ITEMS = [
  { label: "Picks for you", href: "/feed" },
  { label: "Sneakers", href: "/feed?category=Sneakers" },
  { label: "Streetwear", href: "/feed?category=Streetwear" },
  { label: "Apparel", href: "/feed?category=Apparel" },
];

const PUBLIC_LINKS = [
  { label: "About", href: "/about" },
  { label: "Coming Soon", href: "/coming-soon" },
  { label: "Membership", href: "/membership" },
];

type NavLink = {
  label: string;
  href: string;
  items?: { label: string; href: string }[];
};

const MEMBER_LINKS: NavLink[] = [
  { label: "Inventory", href: "/feed", items: INVENTORY_ITEMS },
  { label: "About", href: "/about" },
  { label: "Coming Soon", href: "/coming-soon" },
  { label: "Account", href: "/account" },
];

function NavDropdown({
  label,
  href,
  items,
}: {
  label: string;
  href: string;
  items: { label: string; href: string }[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <Link
        href={href}
        className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
      >
        {label}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute top-full left-0 pt-3 w-52 z-50"
          >
            <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-2 shadow-2xl">
              {items.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="block px-3 py-2.5 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Logo() {
  return (
    <Link href="/" className="flex items-center" aria-label="Clearline home">
      <BrandMark height={22} priority />
    </Link>
  );
}

function BalanceChip({
  powerDollars,
  tier,
  founding,
  active,
}: {
  powerDollars: number;
  tier: number;
  founding: boolean;
  active: boolean;
}) {
  const [open, setOpen] = useState(false);
  const low = powerDollars < 20000;
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        className={`flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full border transition-colors ${
          low
            ? "bg-gradient-to-r from-amber-500/15 to-transparent border-amber-500/40 hover:border-amber-500/70"
            : "bg-gradient-to-r from-blue-600/10 to-transparent border-blue-500/20 hover:border-blue-500/50"
        }`}
      >
        <span
          className={`w-6 h-6 rounded-full flex items-center justify-center ${
            low ? "bg-amber-500" : "bg-blue-600"
          }`}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="w-3 h-3">
            <path d="M3 7h15a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Zm0 0 2-3h11l2 3M16 12h.01" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <span className="text-left">
          <span
            className={`block text-[8px] uppercase tracking-widest leading-none mb-0.5 ${
              low ? "text-amber-400/80" : "text-blue-400/70"
            }`}
          >
            {low ? "Balance low" : "Balance"}
          </span>
          <span className="block font-bold text-sm text-white leading-none tabular-nums">
            ${powerDollars.toLocaleString()}
          </span>
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute top-full right-0 mt-2 w-72 bg-[#0F0F0F] border border-white/10 rounded-2xl p-5 shadow-2xl z-50"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-widest text-gray-500">
                Spending Power
              </span>
              {active ? (
                <span className="text-[9px] text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">
                  Active
                </span>
              ) : (
                <span className="text-[9px] text-gray-400 bg-white/5 px-2 py-0.5 rounded-full">
                  Guest
                </span>
              )}
            </div>
            <p className="font-bold text-4xl text-white tracking-tight mb-1 tabular-nums">
              ${powerDollars.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Available for current drops
            </p>
            {low && (
              <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
                <p className="text-xs font-semibold text-amber-300 mb-1">
                  Your balance is running low
                </p>
                <p className="text-[11px] text-amber-200/70 mb-3">
                  Upgrade your membership to unlock more spending power for
                  upcoming drops.
                </p>
                <Link
                  href="/membership"
                  className="block text-center text-xs font-semibold bg-amber-500 text-black rounded-full py-2 hover:bg-amber-400 transition-colors"
                >
                  Upgrade membership
                </Link>
              </div>
            )}
            <div className="pt-4 border-t border-white/5 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500">Membership</span>
                <span className="text-white">
                  {active ? `Tier ${tier}` : "Not a member"}
                </span>
              </div>
              {active && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className={founding ? "text-amber-400" : "text-white"}>
                      {founding ? "Founding Member" : "Member"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Rate Lock</span>
                    <span className="text-white">Permanent</span>
                  </div>
                </>
              )}
            </div>
            <Link
              href="/account"
              className="mt-4 block text-center text-xs text-gray-400 hover:text-white transition-colors"
            >
              Account
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function NavBar({
  loggedIn,
  isMember,
  isAdmin,
  powerDollars,
  tier,
  founding,
}: {
  loggedIn: boolean;
  isMember: boolean;
  isAdmin: boolean;
  powerDollars: number;
  tier: number;
  founding: boolean;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const showMember = loggedIn;
  const baseLinks = showMember ? MEMBER_LINKS : PUBLIC_LINKS;
  // Membership is always visible, hidden only once the user is subscribed.
  const links = isMember
    ? baseLinks.filter((link) => link.href !== "/membership")
    : baseLinks.some((link) => link.href === "/membership")
      ? baseLinks
      : [...baseLinks, { label: "Membership", href: "/membership" }];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-white/5"
            : "bg-transparent"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo />

            <div className="hidden lg:flex items-center gap-7">
              {links.map((link, i) =>
                link.items ? (
                  <NavDropdown
                    key={`${link.href}-${i}`}
                    label={link.label}
                    href={link.href}
                    items={link.items}
                  />
                ) : (
                  <Link
                    key={`${link.href}-${i}`}
                    href={link.href}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                ),
              )}
              {isAdmin && (
                <Link
                  href="/admin"
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Admin
                </Link>
              )}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <Link
                href="/contact"
                className="px-5 py-2 border border-green-500 text-green-400 text-sm font-semibold rounded-lg hover:bg-green-500/10 transition-colors"
              >
                Sell With Us
              </Link>
              {showMember ? (
                <BalanceChip
                  powerDollars={powerDollars}
                  tier={tier}
                  founding={founding}
                  active={isMember}
                />
              ) : (
                <Link
                  href="/login"
                  className="px-5 py-2 bg-white text-black text-sm font-semibold rounded-full hover:bg-gray-100 transition-colors"
                >
                  Login
                </Link>
              )}
            </div>

            <button
              className="lg:hidden p-2 text-white"
              aria-label="Menu"
              onClick={() => setMenuOpen(true)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#0A0A0A] flex flex-col"
            style={{
              paddingTop: "env(safe-area-inset-top)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            <div className="flex items-center justify-between px-4 h-16 border-b border-white/5">
              <Logo />
              <button
                className="p-2 text-white"
                aria-label="Close"
                onClick={() => setMenuOpen(false)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
                  <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-6 gap-5">
              {links.map((link, i) => (
                <motion.div
                  key={`${link.href}-${i}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                >
                  <Link
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    className="font-bold text-4xl text-white hover:text-blue-400 transition-colors block"
                  >
                    {link.label}
                  </Link>
                  {link.items && (
                    <div className="mt-3 ml-1 flex flex-col gap-2.5">
                      {link.items.slice(1).map((item) => (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={() => setMenuOpen(false)}
                          className="text-lg font-semibold text-gray-400 hover:text-white transition-colors block"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
              {isAdmin && (
                <Link
                  href="/admin"
                  onClick={() => setMenuOpen(false)}
                  className="font-bold text-4xl text-white hover:text-blue-400 transition-colors block"
                >
                  Admin
                </Link>
              )}
            </div>
            <div className="px-6 pb-8 flex items-center justify-between border-t border-white/5 pt-6">
              {showMember ? (
                <>
                  <Link
                    href="/account"
                    onClick={() => setMenuOpen(false)}
                    className="text-gray-400 text-lg font-semibold"
                  >
                    Account
                  </Link>
                  <Link
                    href="/feed"
                    onClick={() => setMenuOpen(false)}
                    className="inline-block px-8 py-3 bg-white text-black font-semibold text-sm rounded-full text-center"
                  >
                    Browse Feed
                  </Link>
                </>
              ) : (
                <Link
                  href="/login"
                  onClick={() => setMenuOpen(false)}
                  className="inline-block px-8 py-3 bg-white text-black font-semibold text-sm rounded-full text-center"
                >
                  Login
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
